'use client'

import { useState } from 'react'
import Image from 'next/image'
import { Download, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { usePwaInstallStore } from '@/stores/pwa-install-store'

const SUBTITLE = 'Faster access from home screen, no space used on your phone.'

/**
 * Renders only once Chrome has actually fired `beforeinstallprompt` (captured by
 * `PwaInstallListener`) and only while the app isn't already installed — there is nothing to
 * prompt on iOS (no such event there) or once `appinstalled` has fired.
 *
 * Mounted once at the app root (`providers/index.tsx`), before `{children}` — not per-shell.
 * `PwaInstallListener` calls `preventDefault()` on every route including logged-out public pages,
 * which suppresses Chrome's own prompt everywhere, so this needs to be reachable on every route too.
 *
 * Two visually distinct variants, toggled purely by Tailwind breakpoints (same dismiss/install
 * logic underneath — a flush full-width bar and a rounded floating card can't share one set of
 * classes):
 * - Mobile: an in-flow, edge-to-edge top bar (same pattern as `OfflineBanner`, rendered before it
 *   in `providers/index.tsx`) — pushes the dashboard shells' own fixed header down rather than
 *   overlaying it, so nothing is ever hidden/covered.
 * - Desktop (`md:` and up): the original fixed bottom-right card, unchanged.
 */
export function InstallAppButton() {
  const deferredPrompt = usePwaInstallStore((state) => state.deferredPrompt)
  const isInstalled = usePwaInstallStore((state) => state.isInstalled)
  const setInstalled = usePwaInstallStore((state) => state.setInstalled)
  const clearDeferredPrompt = usePwaInstallStore((state) => state.clearDeferredPrompt)
  const [isDismissed, setDismissed] = useState(false)

  if (!deferredPrompt || isInstalled || isDismissed) return null

  async function handleInstall() {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') {
      setInstalled()
    } else {
      clearDeferredPrompt()
    }
  }

  return (
    <>
      {/* Mobile: edge-to-edge top bar, in-flow (not fixed) — pushes content down, never overlays it. */}
      <div
        role="dialog"
        aria-label="Install the Nivex app"
        // `relative z-50`: onboarding pages (`onboarding-layout.tsx`) render a
        // `fixed inset-0 bg-background/75` decorative wash over their background image — CSS
        // always paints `position: fixed/absolute/relative` elements above plain `static` ones
        // regardless of DOM order, so without its own position this bar (static by default) was
        // getting washed out underneath that overlay on every onboarding step.
        className="bg-primary text-primary-foreground relative z-50 flex items-center gap-3 px-4 py-2.5 md:hidden"
      >
        {/* The icon artwork itself is indigo (gradient `#93C5FD`→`#444CE7`), so placed directly
            on this same indigo bar it reads as nearly invisible — a solid white backdrop chip
            gives it contrast regardless of what it sits on. */}
        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-white p-1">
          <Image src="/icons/icon-192.png" alt="" width={24} height={24} className="size-6" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium">Get the Nivex app</p>
          <p className="text-primary-foreground/80 text-[11px]">{SUBTITLE}</p>
        </div>
        <Button
          variant="secondary"
          // `secondary`'s default tokens (`bg-secondary`/`text-secondary-foreground`, a very pale
          // lavender-gray) read as a washed-out/disabled button against the vivid `bg-primary`
          // bar — overridden here to a solid white pill with primary-colored text/icon instead,
          // matching the high-contrast "Install" pill in Chrome's own mini-infobar.
          className="text-primary h-8 shrink-0 gap-1.5 bg-white px-2.5 text-xs hover:bg-white/90"
          onClick={() => void handleInstall()}
        >
          <Download className="size-3.5" aria-hidden />
          Install
        </Button>
        <button
          type="button"
          aria-label="Dismiss"
          className="text-primary-foreground/80 hover:text-primary-foreground shrink-0"
          onClick={() => setDismissed(true)}
        >
          <X className="size-3.5" />
        </button>
      </div>

      {/* Desktop: fixed floating card, unchanged. */}
      <div
        role="dialog"
        aria-label="Install the Nivex app"
        className="bg-card fixed bottom-4 right-4 z-40 hidden w-96 items-center gap-3 rounded-2xl border p-3 shadow-lg md:flex"
      >
        <Image
          src="/icons/icon-192.png"
          alt=""
          width={40}
          height={40}
          className="size-10 shrink-0 rounded-lg"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">Get the Nivex app</p>
          <p className="text-muted-foreground text-xs">{SUBTITLE}</p>
        </div>
        <Button size="sm" className="shrink-0 gap-1.5" onClick={() => void handleInstall()}>
          <Download className="size-4" aria-hidden />
          Install
        </Button>
        <button
          type="button"
          aria-label="Dismiss"
          className="text-muted-foreground hover:text-foreground shrink-0"
          onClick={() => setDismissed(true)}
        >
          <X className="size-4" />
        </button>
      </div>
    </>
  )
}
